import { useCallback, useState } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { useUser } from '../../context/UserContext';

export interface ExtractPersonQueryParams {
    taskId: string;
    queryId: string | number;
    notify: string[];
    fields: string[];
}

export interface ExtractPersonQueryResponse {
    status?: string;
    message?: string;
    taskId?: string;
    [key: string]: unknown;
}

const extractApiErrorMessage = (err: unknown): string => {
    if (!(err instanceof Error)) return "Erreur lors de la demande d'export";

    const jsonStart = err.message.indexOf('{');
    if (jsonStart >= 0) {
        try {
            const parsed = JSON.parse(err.message.slice(jsonStart));
            if (typeof parsed?.message === 'string' && parsed.message.trim()) {
                return parsed.message;
            }
            if (typeof parsed?.error === 'string' && parsed.error.trim()) {
                return parsed.error;
            }
        } catch {
            // Keep the original error message below.
        }
    }

    return err.message || "Erreur lors de la demande d'export";
};

export const useExtractPersonQuery = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const { selectedWorkspace } = useUser();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const extractPersonQuery = useCallback(async ({
        taskId,
        queryId,
        notify,
        fields,
    }: ExtractPersonQueryParams): Promise<ExtractPersonQueryResponse | null> => {
        if (!isReady || !taskId || !queryId || notify.length === 0) return null;

        setLoading(true);
        setError(null);

        const numericQueryId = Number(queryId);

        try {
            const response = await authFetch('/putExtractPersonQuery', {
                method: 'PUT',
                body: JSON.stringify({
                    taskId,
                    notify,
                    enseigne: selectedWorkspace?.volumeName,
                    queryId: Number.isNaN(numericQueryId) ? queryId : numericQueryId,
                    fields,
                }),
            }) as ExtractPersonQueryResponse | null;

            if (!response) throw new Error("Réponse invalide lors de la demande d'export");

            if (response.status?.toUpperCase() === 'ERROR') {
                throw new Error(response.message || "Erreur lors de la demande d'export");
            }

            return response;
        } catch (err) {
            const message = extractApiErrorMessage(err);
            setError(message);
            throw new Error(message);
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, selectedWorkspace?.volumeName]);

    const reset = useCallback(() => setError(null), []);

    return { extractPersonQuery, loading, error, reset };
};
